import InsurerLogoImage from "@/shared/components/InsurerLogoImage";
import CurrencySymbol from "@/shared/components/QuoteList/CurrencySymbol";
import {
  getQuoteItemPriceValue,
  isPositiveNumberLike,
  isQuoteItemUnavailable,
  shouldShowQuoteItem,
  shouldUseAmountForDisplay,
} from "@/shared/components/QuoteList/quoteDisplay";
import {
  getExcludingTaxLabel,
  getTaxLabelWithRate,
  LOCALISATION_CONFIG,
} from "@/shared/config/localisation";
import { formatAmountByConfig } from "@/shared/utils/amountFormat";
import { extractInsurerLogoFile } from "@/shared/utils/getInsurarLogo";
import { getCheckedQuoteAddOnItems, getQuotePriceNumber } from "@/shared/utils/quoteAddOns";
import { getNormalizedQuoteSections } from "@/shared/utils/quoteSections";
import { ChevronDown, Edit } from "lucide-react";
import { useMemo, useState } from "react";

export type SelectedQuoteSummaryProps = {
  quote: any | null;
  selectedAddOns?: string[];
  insuredValue?: string | number | null;
  insuredValueLabel?: string;
  showCommission?: boolean;
  showTax?: boolean;
  edit?: boolean;
  onEdit?: (isEdit: boolean) => void;
};

const getDisplayAmount = (value: unknown) => formatAmountByConfig(getQuotePriceNumber(value));

const isNumericValue = (value: unknown) =>
  typeof value === "number" ||
  (typeof value === "string" && /^[+-]?\d+(\.\d+)?$/.test(value.trim()));

const toFiniteNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCoverageRawValue = (item: any) =>
  shouldUseAmountForDisplay(item) || isPositiveNumberLike(getQuoteItemPriceValue(item))
    ? getQuoteItemPriceValue(item)
    : item?.value;

const getAddOnRawValue = (item: any) =>
  item?.amount_without_vat ?? getQuoteItemPriceValue(item) ?? item?.value;

const formatPlanName = (value?: string) => {
  if (!value) {
    return "Selected Plan";
  }

  return value
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getCoverageValue = (item: any) => {
  if (!shouldShowQuoteItem(item)) {
    return null;
  }

  if (item?.value === true && !isPositiveNumberLike(item?.amount)) {
    return "Included";
  }

  const rawValue = getCoverageRawValue(item);

  if (isQuoteItemUnavailable(rawValue)) {
    return "-";
  }

  if (isNumericValue(rawValue)) {
    const amount = getDisplayAmount(rawValue);
    return (
      <span className="inline-flex items-center justify-end gap-1">
        <CurrencySymbol currency={item?.currency} value={getQuotePriceNumber(rawValue)} />
        {amount}
      </span>
    );
  }

  return String(rawValue);
};

export function SelectedQuoteSummary({
  quote,
  selectedAddOns = [],
  insuredValue,
  insuredValueLabel = "Insured Value",
  showCommission = false,
  showTax = true,
  edit = false,
  onEdit,
}: SelectedQuoteSummaryProps) {
  const [showCoverages, setShowCoverages] = useState(false);

  const price = quote?.plan?.price || quote?.price || {};
  const commission = quote?.plan?.commission || quote?.commission || null;
  const commissionAmount = toFiniteNumberOrNull(commission?.amount);
  const commissionPercentage = toFiniteNumberOrNull(commission?.percentage);
  const hasCommission =
    showCommission && (Number.isFinite(commissionAmount) || Number.isFinite(commissionPercentage));
  const premium = price?.amount;
  const currency = price?.currency || LOCALISATION_CONFIG.currencyCode;
  const quoteSections = Array.isArray(quote?.sections) ? quote.sections : [];
  const insurerName = quote?.insurer?.name || "Selected Quote";
  const planName = formatPlanName(quote?.plan?.name);

  const selectedAddOnItems = getCheckedQuoteAddOnItems(quote);

  const fallbackAddOns = selectedAddOns.filter(Boolean).map((label) => ({
    label,
    value: "-",
  }));

  const addOnRows = selectedAddOnItems.length ? selectedAddOnItems : fallbackAddOns;

  const premiumAmount = getQuotePriceNumber(premium);
  const basePremiumExclVat = premiumAmount;

  const addOnTotalExclVat = addOnRows.reduce((sum: number, addon: any) => {
    const addonPrice = getQuotePriceNumber(getAddOnRawValue(addon));

    return sum + addonPrice;
  }, 0);

  const totalPremiumExclVat = basePremiumExclVat + addOnTotalExclVat;
  const totalVat = showTax ? totalPremiumExclVat * LOCALISATION_CONFIG.tax.rate : 0;
  const payableAmount = totalPremiumExclVat + totalVat;
  const excludingTaxSuffix = showTax ? ` (${getExcludingTaxLabel()})` : "";

  const renderCoverageValue = (item: any) => {
    if (!shouldShowQuoteItem(item)) {
      return "-";
    }

    const rawValue = getAddOnRawValue(item);

    if (isQuoteItemUnavailable(rawValue)) {
      return "-";
    }

    if (isNumericValue(rawValue)) {
      return (
        <>
          <CurrencySymbol
            currency={item?.currency || currency}
            value={getQuotePriceNumber(rawValue)}
          />
          {getDisplayAmount(rawValue)}
        </>
      );
    }

    return getCoverageValue(item);
  };
  const visibleSections = useMemo(() => {
    const normalizedSections = getNormalizedQuoteSections(quoteSections, {
      excludeLabels: insuredValue ? ["Car Value"] : [],
    });

    return normalizedSections;
  }, [insuredValue, quoteSections]);

  return (
    <div className="rounded-xl border border-theme-border bg-theme-surface p-5 shadow-[0_6px_18px_rgba(15,23,42,0.08)]">
      <div className="relative mb-4">
        {edit ? (
          <button
            type="button"
            onClick={() => onEdit?.(true)}
            className="absolute right-0 top-0 flex items-center gap-2 text-sm font-semibold text-theme-primary"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        ) : null}

        <div className="flex flex-col items-center text-center">
          <div className="mb-5 flex min-h-20 w-44 items-center justify-center">
            <InsurerLogoImage
              insurerName={insurerName}
              insurerLogoFile={extractInsurerLogoFile(quote?.insurer)}
              alt={insurerName}
              className="max-h-16 max-w-40 w-auto object-contain"
            />
          </div>

          <h2 className="text-[18px] font-bold text-theme-text">{insurerName}</h2>
          <p className="mt-1 text-sm text-theme-text-soft">{planName}</p>
        </div>
      </div>

      <div className="space-y-2">
        {insuredValue ? (
          <div className="flex items-center justify-between text-[14px] text-theme-text-soft">
            <span>{insuredValueLabel}</span>
            <span className="flex items-center gap-1 font-semibold text-theme-primary">
              <CurrencySymbol currency={currency} value={getQuotePriceNumber(insuredValue)} />
              {getDisplayAmount(insuredValue)}
            </span>
          </div>
        ) : null}

        <h3 className="text-[15px] font-bold text-theme-text">Premium Details</h3>

        <div className="flex items-center justify-between text-[14px] text-theme-text-soft">
          <span>Base Premium{excludingTaxSuffix}</span>
          <span className="flex items-center gap-1 font-semibold text-theme-primary">
            <CurrencySymbol currency={currency} value={basePremiumExclVat} />
            {getDisplayAmount(basePremiumExclVat)}
          </span>
        </div>

        {addOnRows.map((addon: any, index: number) => (
          <div
            key={`${addon?.label}-${index}`}
            className="flex items-center justify-between text-[14px] text-theme-text-soft"
          >
            <span>
              {addon?.label}
              {excludingTaxSuffix}
            </span>
            <span className="flex items-center gap-1 font-semibold text-theme-primary">
              {renderCoverageValue(addon)}
            </span>
          </div>
        ))}

        {hasCommission ? (
          <div className="flex items-center justify-between text-[14px] text-theme-text-soft">
            <span className="font-medium text-theme-text">
              Commission{" "}
              <span className="font-medium text-theme-text">
                ({Number.isFinite(commissionPercentage) ? `${commissionPercentage}%` : "-"})
              </span>
            </span>

            <span className="flex items-center gap-1 font-semibold text-theme-primary">
              <CurrencySymbol
                currency={commission?.currency || currency}
                value={commissionAmount ?? 0}
              />
              {getDisplayAmount(commissionAmount ?? 0)}
            </span>
          </div>
        ) : null}

        <div className="border-t border-theme-border pt-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-[14px] text-theme-text-soft">
              <span>Total Premium{excludingTaxSuffix}</span>
              <span className="flex items-center gap-1 font-semibold text-theme-primary">
                <CurrencySymbol currency={currency} value={totalPremiumExclVat} />
                {getDisplayAmount(totalPremiumExclVat)}
              </span>
            </div>

            {showTax ? (
              <div className="flex items-center justify-between text-[14px] text-theme-text-soft">
                <span>{getTaxLabelWithRate()}</span>
                <span className="flex items-center gap-1 font-semibold text-theme-primary">
                  <CurrencySymbol currency={currency} value={totalVat} />
                  {getDisplayAmount(totalVat)}
                </span>
              </div>
            ) : null}

            <div className="mt-2 flex items-center justify-between">
              <span className="text-[16px] font-bold text-theme-text">Payable Amount</span>
              <span className="flex items-center gap-1 text-[16px] font-bold text-theme-primary">
                <CurrencySymbol currency={currency} value={payableAmount} />
                {getDisplayAmount(payableAmount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 border-t border-theme-border pt-4">
        <button
          type="button"
          onClick={() => setShowCoverages((current) => !current)}
          className="flex w-full items-center justify-between"
        >
          <span className="text-[13px] font-bold uppercase text-theme-primary">Coverages</span>

          <ChevronDown
            className={`h-6 w-6 text-theme-text-soft transition-transform duration-200 ${
              showCoverages ? "rotate-180" : ""
            }`}
          />
        </button>

        {showCoverages ? (
          <div className="mt-5 space-y-5">
            {visibleSections.map((section: any) => (
              <div key={section.title}>
                <h4 className="border-b border-theme-border pb-2 text-[15px] font-bold text-theme-primary">
                  {section.title}
                </h4>

                <div className="mt-3 space-y-3">
                  {section.items.map((item: any, index: number) => (
                    <div
                      key={`${item.label}-${index}`}
                      className="flex items-start justify-between gap-4"
                    >
                      <div className="text-[14px] leading-5 text-theme-text-soft">{item.label}</div>

                      <div className="max-w-[210px] text-right text-[14px] font-semibold leading-5 text-theme-text">
                        {getCoverageValue(item)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
